import { useTranslation } from 'react-i18next'

import { TrashIcon } from '@phosphor-icons/react'

import { IconButton } from '@reveni-io/reveni-ui'

import { currencyFields, displayMatchTypes, inputCategories } from '@/common/components/MatchTypeEditor/constants'
import { getInputCategory, getMatchTypeOptions, getPropertyOptions } from '@/common/components/MatchTypeEditor/helpers'

import CriterionFields from './CriterionFields/CriterionFields'
import { getDefaultMatchValues, isBooleanField } from './helpers'
import * as Styles from './styles'

const CriterionEditor = ({
  criterion,
  index,
  properties,
  onChange,
  onRemove,
  disabled,
  hasError,
  currencies,
  storeCurrencyCode,
  inputBackgroundColor,
}) => {
  const { t } = useTranslation()

  const propertyOptions = getPropertyOptions(properties, t)
  const fieldConfig = properties[criterion.field]
  const matchTypeOptions = getMatchTypeOptions(fieldConfig, t)
  const comboItems = fieldConfig?.comboItems || []
  const showCombo = comboItems.length > 0
  const hierarchical = Boolean(fieldConfig?.hierarchical)
  const showRemoveButton = Boolean(onRemove) && !disabled

  const handleChangeField = value => {
    const newFieldConfig = properties[value]

    onChange(index, {
      ...criterion,
      field: value,
      match_type: isBooleanField(newFieldConfig) ? displayMatchTypes.boolean : '',
      match_value: '',
      match_values: null,
      match_case: false,
      date_range_start: null,
      date_range_end: null,
      days_value: null,
      min_value: null,
      max_value: null,
      match_currency: currencyFields.includes(value) ? storeCurrencyCode : null,
    })
  }

  const handleChangeMatchType = displayKey => {
    onChange(index, {
      ...criterion,
      match_type: displayKey,
      match_value: '',
      match_values: getDefaultMatchValues(displayKey),
      match_case: false,
      date_range_start: null,
      date_range_end: null,
      days_value: null,
      min_value: null,
      max_value: null,
    })
  }

  const handleChangeValue = value => {
    const category = getInputCategory(criterion.match_type, criterion.field)

    if (category === inputCategories.multiValue || category === inputCategories.region) {
      onChange(index, { ...criterion, match_values: value })
      return
    }

    if (category === inputCategories.dateRange) {
      onChange(index, { ...criterion, date_range_start: value.startDate, date_range_end: value.endDate })
      return
    }

    if (category === inputCategories.daysElapsed) {
      onChange(index, { ...criterion, days_value: value })
      return
    }

    if (category === inputCategories.range) {
      onChange(index, { ...criterion, ...value })
      return
    }

    onChange(index, { ...criterion, match_value: value })
  }

  const handleChangeMatchCase = value => onChange(index, { ...criterion, match_case: value })

  const handleChangeMatchCurrency = value => onChange(index, { ...criterion, match_currency: value })

  return (
    <Styles.Container>
      {showRemoveButton && (
        <Styles.Actions>
          <IconButton variant="text" icon={<TrashIcon size={24} />} onClick={onRemove} />
        </Styles.Actions>
      )}
      <CriterionFields
        propertyOptions={propertyOptions}
        matchTypeOptions={matchTypeOptions}
        criterion={criterion}
        onChangeField={handleChangeField}
        onChangeMatchType={handleChangeMatchType}
        onChangeValue={handleChangeValue}
        onChangeMatchCase={handleChangeMatchCase}
        onChangeMatchCurrency={handleChangeMatchCurrency}
        disabled={disabled}
        showCombo={showCombo}
        comboItems={comboItems}
        hierarchical={hierarchical}
        hasError={hasError}
        propertyDescription={fieldConfig?.description}
        currencies={currencies}
        storeCurrencyCode={storeCurrencyCode}
        inputBackgroundColor={inputBackgroundColor}
      />
    </Styles.Container>
  )
}

export default CriterionEditor
